import { useCallback, useEffect, useMemo } from 'react';
import { buildCalendarDateMeta } from '../../../utils/dateUtils';
import { normalizeSelectionBrands } from '../../../utils/brands';
import { computeSelectionKey } from '../../../utils/selectionKey';
import { formatDate } from '../utils/dates';
import { pickSelectionValue } from '../utils/selectionMeta';
import { useDiHydration } from './useDiHydration';
import { DETAIL_PANEL_TAB_EDIT_AND_SEND } from '../constants/detailPanelTabs';
import {
  isEnrollmentTerminated,
  resolveApiPresenceStatus,
  resolveEnrollmentLifecycleStatus,
} from '../../../utils/enrollmentEvidence';

// This hook builds the normalized selection-row model used by the detail drawer.
// It bridges raw enrollment product selections, AppSheet/DI enrichment, and the
// "jump into edit-and-send" selection workflow.
const useDetailPanelSelectionsModel = ({
  currentRow,
  programCode,
  programKey,
  selectionKeyTargetRef,
  selectedSelectionIndex,
  setSelectedSelectionIndex,
  setActiveTab,
  fetchDiOrderItemsByReferenceId,
  setSnackbar,
}) => {
  const rawSelections = useMemo(
    () => (Array.isArray(currentRow?.productSelections) ? currentRow.productSelections : []),
    [currentRow?.productSelections]
  );

  const { diByReferenceId } = useDiHydration({
    selections: rawSelections,
    fetchDiOrderItemsByReferenceId,
    setSnackbar,
  });

  useEffect(() => {
    const targetKey = selectionKeyTargetRef.current;
    if (!targetKey || rawSelections.length === 0) return;

    const bacForKey = currentRow?.bac != null ? String(currentRow.bac).trim() : '';
    const programCodeForKey = String(currentRow?.programCode || currentRow?.program || programCode || '').trim();
    if (!bacForKey || !programCodeForKey) return;

    // Selection key targeting lets the drawer land on a specific selection even
    // when the user entered from another workflow that only knew the stable key.
    const idx = rawSelections.findIndex((selection) => {
      try {
        return computeSelectionKey(selection, { bac: bacForKey, programCode: programCodeForKey }) === targetKey;
      } catch (_err) {
        return false;
      }
    });

    if (idx >= 0 && idx !== selectedSelectionIndex) {
      setSelectedSelectionIndex(idx);
    }
  }, [
    rawSelections,
    currentRow?.bac,
    currentRow?.programCode,
    currentRow?.program,
    programCode,
    selectedSelectionIndex,
    selectionKeyTargetRef,
    setSelectedSelectionIndex,
  ]);

  const productSelections = useMemo(
    () =>
      rawSelections.map((selection, index) => {
        const brands = normalizeSelectionBrands(
          selection.brands ?? selection.brandCodes ?? selection.brandNames ?? selection.programBrands
        );

        const vendorProvidedDate = buildCalendarDateMeta(
          pickSelectionValue(selection, [
            'vendorProvidedDate',
            'vendorProvidedDt',
            'vendorProvidedAt',
            'vendorProvidedTimestamp',
          ])
        );
        const productSelectionDate = buildCalendarDateMeta(
          pickSelectionValue(selection, ['productSelectionDate', 'selectionDate', 'selectedAt', 'selectionDt'])
        );
        const goLiveDate = buildCalendarDateMeta(
          pickSelectionValue(selection, ['goLiveDate', 'goLiveDt', 'goLiveAt', 'liveDate', 'liveDt'])
        );
        const cancelDate = buildCalendarDateMeta(
          pickSelectionValue(selection, ['cancelDate', 'cancelDt', 'cancelAt', 'cancelledAt'])
        );
        const billingStart = buildCalendarDateMeta(
          pickSelectionValue(selection, ['billingStartDate', 'billingStart', 'billingStartDt', 'billingStartAt'])
        );
        const billingEnd = buildCalendarDateMeta(
          pickSelectionValue(selection, ['billingEndDate', 'billingEnd', 'billingEndDt', 'billingEndAt'])
        );
        const effectiveDate = buildCalendarDateMeta(
          pickSelectionValue(selection, ['effectiveDate', 'effectiveDt', 'effectiveAt', 'effectiveOn'])
        );

        const referenceId = selection?.referenceId != null ? String(selection.referenceId).trim() : '';
        const di = referenceId ? diByReferenceId?.[referenceId] : null;

        return {
          id: index,
          ...selection,
          bac: currentRow?.bac ?? '',
          dealerName: currentRow?.dealerName ?? currentRow?.dealer?.dealerName ?? '',
          programCode: programCode || currentRow?.programCode || currentRow?.program || '',
          programName: programKey || currentRow?.programName || currentRow?.programLabel || '',
          enrollmentStatusCd: resolveEnrollmentLifecycleStatus(currentRow) || 'LIFECYCLE_UNAVAILABLE',
          apiPresenceStatus: resolveApiPresenceStatus(currentRow),
          isTerminated: isEnrollmentTerminated(currentRow),
          syntheticCancellation: currentRow?.syntheticCancellation,
          syntheticCancellationState: currentRow?.syntheticCancellationState,
          terminationDateDisplay:
            formatDate(
              currentRow?.terminationDate ??
                currentRow?.isTerminatedDate ??
                currentRow?.termDate ??
                currentRow?.terminationDt
            ) || '',
          enrollmentDateDisplay:
            formatDate(currentRow?.enrollmentDate ?? currentRow?.enrolledDt ?? currentRow?.enrollmentDt) || '',
          optOutDateDisplay: formatDate(currentRow?.optOutDt ?? currentRow?.optOutDate) || '',
          sourceSelection: selection,
          _selectionIndex: index,
          vendorInvoiceNumber: selection.vendorInvoiceNumber || selection.vendorInvoiceNbr || selection.invoiceNumber || '',
          productBrandCode: selection.productBrandCode || selection.productBrandCd || selection.productCd || '',
          brandsDisplay: brands.display,
          brandsCodes: brands.codes,
          vendorProvidedDateDisplay: vendorProvidedDate.display,
          vendorProvidedDateSort: vendorProvidedDate.sort,
          productSelectionDateDisplay: productSelectionDate.display,
          productSelectionDateSort: productSelectionDate.sort,
          goLiveDateDisplay: goLiveDate.display,
          goLiveDateSort: goLiveDate.sort,
          cancelDateDisplay: cancelDate.display,
          cancelDateSort: cancelDate.sort,
          billingStartDateDisplay: billingStart.display,
          billingStartDateSort: billingStart.sort,
          billingEndDateDisplay: billingEnd.display,
          billingEndDateSort: billingEnd.sort,
          effectiveDateDisplay: effectiveDate.display,
          effectiveDateSort: effectiveDate.sort,
          // DI fields are merged last so the detail table can prefer the freshest
          // AppSheet-side metadata without losing the original source selection.
          ...(di || {}),
        };
      }),
    [currentRow, diByReferenceId, programCode, programKey, rawSelections]
  );

  useEffect(() => {
    if (rawSelections.length === 0) {
      setSelectedSelectionIndex(0);
      return;
    }
    if (selectedSelectionIndex >= rawSelections.length) {
      setSelectedSelectionIndex(0);
    }
  }, [rawSelections, selectedSelectionIndex, setSelectedSelectionIndex]);

  const handleSelectionRowClick = useCallback(
    (params) => {
      const nextIndex = typeof params?.id === 'number' ? params.id : params?.row?._selectionIndex ?? 0;
      // Clicking a selection is treated as intent to act on that selection, so
      // the drawer pivots straight into the edit-and-send tab.
      setSelectedSelectionIndex(nextIndex);
      setActiveTab(DETAIL_PANEL_TAB_EDIT_AND_SEND);
    },
    [setActiveTab, setSelectedSelectionIndex]
  );

  const handleDetailPaneSelectionRowClick = useCallback(
    ({ row }) => {
      const nextIndex = row?._selectionIndex ?? row?.id ?? 0;
      handleSelectionRowClick({ id: nextIndex, row });
    },
    [handleSelectionRowClick]
  );

  return {
    rawSelections,
    productSelections,
    handleDetailPaneSelectionRowClick,
  };
};

export default useDetailPanelSelectionsModel;
